/**
 * RoadtripPage — Aussie road-trip planner (iter 245).
 *
 * Owner ask: "Plot a route from A → B using the member's EV range, drop
 * charging stops before the battery runs out, tell them if the charger
 * is fast/standard + wait time, add optional overnight suggestions (BnB
 * / hotel / caravan park). Booking.com affiliate wiring for the future."
 *
 * Backend: GET /api/roadtrip/plan?from=&to=&range_km=&connector=&sleepover=
 *          &accom_type=&daily_hours=&fast_only=
 * Member EV: GET/POST /api/users/me/ev-profile — auto-fills range_km +
 *            connector for logged-in members.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Zap, Bed, Home, Tent, MapPin, Loader2, Route, Car } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

/** Fuzzy place-autocomplete for the From / To inputs. Hits our own
 *  /api/roadtrip/places endpoint (which searches every Aussie suburb
 *  in hp_pois with typo tolerance — "newcstle" → "Newcastle").
 *  On pick we submit `lat,lng` so the planner geocodes deterministically. */
function PlaceAutocomplete({ value, onChange, placeholder, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi]     = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) { setItems([]); return; }
    // Debounce 180ms — snappy without hammering the API.
    const timer = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const r = await fetch(
          `${API}/api/roadtrip/places?q=${encodeURIComponent(q)}&limit=8`,
          { signal: abortRef.current.signal });
        if (!r.ok) return;
        const d = await r.json();
        setItems(d.items || []);
      } catch (_e) { /* ignore fetch aborts / network */ }
    }, 180);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    const off = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const pick = (it) => {
    // Submit lat,lng so the plan endpoint always geocodes the exact
    // same point regardless of typos in the raw input.
    onChange(`${it.lat.toFixed(6)},${it.lng.toFixed(6)}`, it.label);
    setOpen(false); setHi(-1); setItems([]);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(-1); }}
        onFocus={(e) => {
          // Select all existing content so the user can just start
          // typing to replace it — no backspacing needed. Also open the
          // suggestions dropdown if we have cached items for the value.
          try { e.target.select(); } catch { /* older browsers */ }
          if (items.length) setOpen(true);
        }}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
      />
      {open && items.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 top-full mt-1 left-0 right-0 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg max-h-72 overflow-y-auto"
        >
          {items.map((it, i) => (
            <li key={`${it.name}-${it.state}-${it.postcode}-${i}`}>
              <button
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                onMouseEnter={() => setHi(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  i === hi
                    ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200"
                    : "hover:bg-stone-50 dark:hover:bg-stone-800 text-stone-800 dark:text-stone-200"
                }`}
              >
                <MapPin className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"/>
                <span className="truncate">{it.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KIND_META = {
  ev_charging:  { Icon: Zap,   color: "text-amber-600 dark:text-amber-400" },
  sleepover:    { Icon: Bed,   color: "text-purple-700 dark:text-purple-400" },
  caravan_park: { Icon: Tent,  color: "text-emerald-700 dark:text-emerald-400" },
  bnb:          { Icon: Home,  color: "text-rose-700 dark:text-rose-400" },
  hotel:        { Icon: Bed,   color: "text-blue-700 dark:text-blue-400" },
};

/** Turn a raw minute count into human speech.
 *   45  → "45 min"
 *   90  → "1 h 30 min"
 *   180 → "3 h"
 *   326 → "5 h 26 min"
 * Deliberately concise — we render it inline alongside distance. */
function formatDuration(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} h` : `${h} h ${rem} min`;
}

/** Hours-as-decimal → same human format. */
function formatHours(hours) {
  return formatDuration(Math.round(Number(hours || 0) * 60));
}

/** ISO datetime → "Fri 20 Feb · 08:00" (traveller-friendly).
 *  Empty / bad → "—" so we never show "NaN" on the UI. */
function formatDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-AU", {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
}

function StopCard({ stop }) {
  const { t } = useTranslation();
  const meta = KIND_META[stop.kind] || KIND_META.ev_charging;
  const Icon = meta.Icon;
  const isCharger = stop.kind === "ev_charging";
  const overRange = !!stop.leg_exceeds_range;
  // Prefer the backend's reverse-geocoded friendly line so the user
  // never sees raw lat/lng ("~15 km NE of Singleton, NSW 2330").
  const addressLine =
    stop.friendly_location ||
    [stop.address, stop.suburb, stop.postcode].filter(Boolean).join(", ") ||
    "Location details unavailable";
  return (
    <div
      data-testid={`roadtrip-stop-${stop.slug || stop.source_id}`}
      className={`flex gap-4 p-4 rounded-xl border ${
        overRange
          ? "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40"
          : "border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900"
      }`}
    >
      <div className={`shrink-0 flex flex-col items-center ${meta.color}`}>
        <Icon className="w-6 h-6" strokeWidth={1.75} />
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1">
          {isCharger ? `Stop ${stop.order}` : `Night ${stop.night}`}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-stone-900 dark:text-stone-100 truncate">
          {!isCharger && stop.slug ? (
            <Link
              to={`/hotels/${encodeURIComponent(stop.slug)}`}
              data-testid={`roadtrip-hotel-link-${stop.slug}`}
              className="text-[#C05746] hover:text-[#A64A3C] hover:underline
                          underline-offset-2"
              title="View hotel details"
            >
              {stop.name}
            </Link>
          ) : (
            stop.name
          )}
          {!isCharger && stop.has_ev_charger && (
            <span
              data-testid="roadtrip-sleepover-charger-icon"
              title={
                stop.nearby_charger?.name
                  ? `EV charger nearby: ${stop.nearby_charger.name}${
                      stop.nearby_charger.distance_m
                        ? ` (~${stop.nearby_charger.distance_m}m)`
                        : ""
                    }`
                  : "EV charger available at or near this stay"
              }
              className="ml-1.5 inline-block align-middle text-amber-500"
              aria-label="EV charger available"
            >
              ⚡
            </span>
          )}
        </p>
        {!isCharger && stop.community_rating?.count > 0 && (
          <p
            data-testid="roadtrip-sleepover-community-rating"
            className="text-[11px] font-medium text-amber-600 dark:text-amber-400 mt-0.5 leading-none"
            title={`HustleSuite community rating: ${stop.community_rating.avg} from ${stop.community_rating.count} reviews`}
            aria-label={`Community rating ${stop.community_rating.avg} out of 5, ${stop.community_rating.count} reviews`}
          >
            {"★".repeat(Math.floor(Number(stop.community_rating.avg) || 0))}
            {(Number(stop.community_rating.avg) % 1) >= 0.5 ? "½" : ""}
            <span className="ml-1 text-stone-500 dark:text-stone-400 text-[10px] align-middle">
              {stop.community_rating.avg} · {stop.community_rating.count} review{stop.community_rating.count === 1 ? "" : "s"}
            </span>
          </p>
        )}
        {!isCharger && !stop.community_rating?.count && !!stop.stars && (
          <p
            data-testid="roadtrip-sleepover-stars"
            className="text-[11px] font-medium text-amber-600 dark:text-amber-400 mt-0.5 leading-none"
            title={`${stop.stars} star rating`}
            aria-label={`${stop.stars} star rating`}
          >
            {"★".repeat(Math.floor(Number(stop.stars) || 0))}
            {(Number(stop.stars) % 1) >= 0.5 ? "½" : ""}
            <span className="ml-1 text-stone-500 dark:text-stone-400 text-[10px] align-middle">
              {stop.stars}
            </span>
          </p>
        )}
        <p className="text-[12px] text-stone-500 dark:text-stone-400 truncate">
          {addressLine}
        </p>
        {overRange && (
          <p
            data-testid="roadtrip-stop-range-warning"
            className="mt-2 text-[11px] font-semibold text-red-700 dark:text-red-300 flex items-start gap-1.5 leading-snug"
          >
            <span aria-hidden="true">⚠️</span>
            <span>
              This leg is <b>{stop.leg_km} km</b>, which exceeds your set range
              — you would run out of power roughly <b>{stop.gap_km_over} km</b>
              {" "}before reaching this charger. Reduce range assumption or
              add an intermediate top-up.
            </span>
          </p>
        )}
        <p className="text-[11px] text-stone-500 dark:text-stone-400 mt-1">
          {isCharger ? (
            <>
              🚗 {stop.leg_km} km leg · ⏱ {formatDuration(stop.drive_time_min)} drive · ⚡ {stop.fast_label} · 🕐 {stop.dwell_min} min charge
              {" "}· 📍 {stop.km_from_origin} km from start
            </>
          ) : (
            <>
              🛏 {stop.sub_niche} · 📍 {stop.km_from_origin} km from start
              {stop.has_ev_charger && (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                  ⚡ {t("rt.charger_onsite", { defaultValue: "Charger on-site" })}
                </span>
              )}
            </>
          )}
        </p>
        {/* Real clock ETA if a schedule was set. */}
        {(stop.arrival_iso || stop.checkin_iso) && (
          <p
            data-testid="roadtrip-stop-eta"
            className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 mt-1"
          >
            {isCharger ? (
              <>🕒 Arrive {formatDT(stop.arrival_iso)} · leave {formatDT(stop.departure_iso)}</>
            ) : (
              <>
                🕒 Arrive {formatDT(stop.arrival_iso)}
                {stop.waited_until_checkin_iso && stop.waited_until_checkin_iso !== stop.arrival_iso && (
                  <> · check-in {formatDT(stop.checkin_iso)}</>
                )}
                {" "}· check-out {formatDT(stop.checkout_iso)}
                {stop.overnight_hours ? <> ({stop.overnight_hours} h)</> : null}
              </>
            )}
          </p>
        )}
        {stop.book_url && !isCharger && (
          <a
            href={stop.book_url}
            target="_blank"
            rel="noreferrer noopener sponsored"
            data-testid={`roadtrip-book-${stop.slug || stop.source_id}`}
            className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            {t("rt.check_price_book", { defaultValue: "Check price & book on Trivago ↗" })}
          </a>
        )}
        {stop.website && (
          <a
            href={stop.website}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            {t("rt.visit_site", { defaultValue: "Visit site ↗" })}
          </a>
        )}
      </div>
    </div>
  );
}

export default function RoadtripPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({
    from:       params.get("from") || "Brisbane",
    to:         params.get("to")   || "Hobart",
    range_km:   Number(params.get("range_km") || 400),
    safety_pct: Number(params.get("safety_pct") || 85),
    connector:  params.get("connector") || "",
    fast_only:  params.get("fast_only") === "true",
    sleepover:  params.get("sleepover") !== "false",
    accom_type: params.get("accom_type") || "any",
    min_stars:  Number(params.get("min_stars") || 0),
    hotel_charger_only: params.get("hotel_charger_only") === "true",
    pet_friendly:       params.get("pet_friendly") === "true",
    daily_hours: Number(params.get("daily_hours") || 8),
    tier:       params.get("tier") || "free",
    schedule_mode: params.get("arrive_by") ? "arrive" : "depart",
    depart_at:  params.get("depart_at") || "",
    arrive_by:  params.get("arrive_by") || "",
    checkin_time:  params.get("checkin_time")  || "15:00",
    checkout_time: params.get("checkout_time") || "10:00",
    // Family / attractions (iter 264)
    adults:   Number(params.get("adults") || 1),
    kids:     Number(params.get("kids") || 0),
    kid_ages: params.get("kid_ages") || "",
    include_attractions:    params.get("include_attractions") === "true",
    include_kid_activities: params.get("include_kid_activities") === "true",
  });
  // Human-readable labels that shadow the raw lat,lng in form.from/to
  // so the user always sees "Newcastle, NSW 2300" not "-32.9,151.7".
  // If the URL param happens to BE a lat,lng (deep-link), start with a
  // friendly placeholder and let the first successful plan overwrite.
  const _looksLikeLatLng = (s) => /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test((s || "").trim());
  const initFrom = params.get("from") || "Brisbane";
  const initTo   = params.get("to")   || "Hobart";
  const [fromLabel, setFromLabel] = useState(
    _looksLikeLatLng(initFrom) ? "Resolving…" : initFrom);
  const [toLabel,   setToLabel]   = useState(
    _looksLikeLatLng(initTo)   ? "Resolving…" : initTo);
  // Ordered waypoint list — {raw:string, label:string} pairs.
  const initWps = (params.get("waypoints") || "").split("|").filter(Boolean);
  const [waypoints, setWaypoints] = useState(
    initWps.map((v) => ({
      raw: v,
      // Guard: never surface bare "lat,lng" as the visible label.
      // The label will be updated with a friendly suburb once the
      // first plan completes.
      label: _looksLikeLatLng(v) ? "Resolving…" : v,
    })));
  const [plan, setPlan]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState(null);
  const [savedTrips, setSavedTrips] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [remainingKm, setRemainingKm] = useState(null);
  const [showResume, setShowResume] = useState(false);
  // Selected suggested-detour slugs (persisted between replans).
  const [selectedDetours, setSelectedDetours] = useState(
    (params.get("selected_stops") || "").split(",").filter(Boolean));

  const wpLimit = { free: 5, pro: 20, admin: 50 }[form.tier] || 5;

  // Prefill from the member's saved EV profile if logged in.
  useEffect(() => {
    fetch(`${API}/api/users/me/ev-profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const ep = d?.ev_profile;
        if (!ep) return;
        setForm((f) => ({
          ...f,
          range_km:  ep.range_km  || f.range_km,
          connector: ep.connector || f.connector,
        }));
      })
      .catch(() => {});
  }, []);

  const runPlan = async (overrides = {}) => {
    setLoading(true); setErr(null);
    try {
      const qs = new URLSearchParams();
      qs.set("from", form.from);
      qs.set("to",   form.to);
      qs.set("range_km", String(form.range_km));
      qs.set("safety_pct", String(form.safety_pct));
      qs.set("tier", form.tier);
      if (form.connector) qs.set("connector", form.connector);
      qs.set("fast_only",   String(form.fast_only));
      qs.set("sleepover",   String(form.sleepover));
      qs.set("accom_type",  form.accom_type);
      if (form.min_stars && form.min_stars > 0)
        qs.set("min_stars", String(form.min_stars));
      qs.set("hotel_charger_only", String(form.hotel_charger_only));
      qs.set("pet_friendly", String(form.pet_friendly));
      qs.set("daily_hours", String(form.daily_hours));
      // Schedule: either depart_at OR arrive_by (mutually exclusive on
      // the backend). Only send when the user has picked one.
      if (form.schedule_mode === "depart" && form.depart_at)
        qs.set("depart_at", form.depart_at);
      if (form.schedule_mode === "arrive" && form.arrive_by)
        qs.set("arrive_by", form.arrive_by);
      if (form.checkin_time)  qs.set("checkin_time",  form.checkin_time);
      if (form.checkout_time) qs.set("checkout_time", form.checkout_time);
      qs.set("adults", String(form.adults));
      qs.set("kids",   String(form.kids));
      if (form.kid_ages) qs.set("kid_ages", form.kid_ages);
      qs.set("include_attractions",    String(form.include_attractions));
      qs.set("include_kid_activities", String(form.include_kid_activities));
      if (selectedDetours.length)
        qs.set("selected_stops", selectedDetours.join(","));
      const wpJoined = waypoints.map((w) => w.raw).filter(Boolean).join("|");
      if (wpJoined) qs.set("waypoints", wpJoined);
      if (overrides.remaining_km != null)
        qs.set("remaining_km", String(overrides.remaining_km));
      setParams(qs);
      // Cache for Driving Mode to reuse.
      try { window.localStorage.setItem("hs-roadtrip-last-qs", qs.toString()); } catch { /* noop */ }
      // Cache-bust: (a) unique `_ts` query param bypasses Cloudflare's
      // response cache entirely, (b) `cache: 'no-store'` tells the
      // browser to never serve from its own memory/disk cache. Users
      // reported stale itineraries after we ingested new charger/hotel
      // data — this closes both cache layers.
      qs.set("_ts", String(Date.now()));
      const r = await fetch(`${API}/api/roadtrip/plan?${qs.toString()}`, {
        cache:   "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Plan failed");
      const planData = await r.json();
      setPlan(planData);
      // If the URL params carried raw lat,lng (e.g. deep-linked from
      // HustleFinder or a saved trip), sync the label inputs back so
      // the UI never displays coordinates. Guard: only accept labels
      // that are NOT themselves lat/lng strings.
      if (planData?.from?.label && !_looksLikeLatLng(planData.from.label))
        setFromLabel(planData.from.label);
      if (planData?.to?.label && !_looksLikeLatLng(planData.to.label))
        setToLabel(planData.to.label);
      // Same for intermediate waypoints — the backend reverse-geocodes
      // any raw lat/lng inputs to a friendly suburb string. Match by
      // the `input` field (the raw value we sent) so re-orderings and
      // duplicates map correctly.
      if (Array.isArray(planData?.waypoints) && planData.waypoints.length) {
        setWaypoints((wps) => wps.map((w) => {
          const match = planData.waypoints.find((pw) => pw.input === w.raw);
          if (match && match.label && !_looksLikeLatLng(match.label)) {
            return { ...w, label: match.label };
          }
          return w;
        }));
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reverseTrip = () => {
    setForm((f) => ({ ...f, from: f.to, to: f.from }));
    setFromLabel(toLabel); setToLabel(fromLabel);
    setWaypoints((wps) => [...wps].reverse());
    setTimeout(runPlan, 50);
  };

  const addWaypoint = () => {
    if (waypoints.length >= wpLimit) return;
    setWaypoints((wps) => [...wps, { raw: "", label: "" }]);
  };
  const removeWaypoint = (idx) => {
    setWaypoints((wps) => wps.filter((_, i) => i !== idx));
  };
  const setWaypointAt = (idx, raw, label) => {
    // Never keep bare "lat,lng" as the visible label. If the picker
    // handed us a friendly label, use it; otherwise show a placeholder
    // that the /plan response will replace with a reverse-geocoded name.
    const safeLabel = label && !_looksLikeLatLng(label)
      ? label
      : (_looksLikeLatLng(raw) ? "Selected location" : raw);
    setWaypoints((wps) => wps.map((w, i) => (i === idx
      ? { raw, label: safeLabel } : w)));
  };

  const loadSavedTrips = async () => {
    try {
      const r = await fetch(`${API}/api/roadtrip/trips`,
                             { credentials: "include" });
      if (!r.ok) { setSavedTrips([]); return; }
      const d = await r.json();
      setSavedTrips(d.items || []);
    } catch { setSavedTrips([]); }
  };

  const saveTrip = async () => {
    const name = (saveName || "").trim();
    if (!name) return;
    try {
      const body = {
        name,
        // Persist the friendly label so reload never shows lat/lng
        // in the input or summary.
        from: _looksLikeLatLng(form.from)
              ? (plan?.from?.label || fromLabel || form.from)
              : form.from,
        to:   _looksLikeLatLng(form.to)
              ? (plan?.to?.label || toLabel || form.to)
              : form.to,
        waypoints: waypoints.map((w) => w.raw).filter(Boolean),
        range_km: form.range_km, safety_pct: form.safety_pct,
        connector: form.connector, fast_only: form.fast_only,
        sleepover: form.sleepover, accom_type: form.accom_type,
        min_stars: form.min_stars || 0,
        hotel_charger_only: form.hotel_charger_only,
        daily_hours: form.daily_hours, tier: form.tier,
      };
      const r = await fetch(`${API}/api/roadtrip/trips`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      setSaveOpen(false); setSaveName("");
      loadSavedTrips();
    } catch (e) { setErr(e.message); }
  };

  const loadTrip = (t) => {
    setForm((f) => ({
      ...f,
      from: t.from, to: t.to,
      range_km: t.range_km, safety_pct: t.safety_pct,
      connector: t.connector || "", fast_only: !!t.fast_only,
      sleepover: !!t.sleepover, accom_type: t.accom_type || "any",
      min_stars: Number(t.min_stars || 0),
      hotel_charger_only: !!t.hotel_charger_only,
      daily_hours: t.daily_hours, tier: t.tier || "free",
    }));
    setFromLabel(t.from); setToLabel(t.to);
    setWaypoints((t.waypoints || []).map((v) => ({
      raw: v,
      label: _looksLikeLatLng(v) ? "Resolving…" : v,
    })));
    setTimeout(runPlan, 50);
  };

  const deleteTrip = async (id) => {
    if (!window.confirm("Delete this saved trip?")) return;
    await fetch(`${API}/api/roadtrip/trips/${id}`, {
      method: "DELETE", credentials: "include" });
    loadSavedTrips();
  };

  const resumeFromHere = () => {
    if (!("geolocation" in navigator)) {
      setErr("Geolocation not available on this device.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((f) => ({ ...f, from: `${latitude.toFixed(6)},${longitude.toFixed(6)}` }));
        setFromLabel("Current location");
        setShowResume(true);
      },
      (e) => setErr("Location error: " + e.message),
      { enableHighAccuracy: true, timeout: 10000 });
  };

  const applyResume = () => {
    const rk = Number(remainingKm);
    if (!rk || rk < 20) { setErr("Enter your remaining range in km (≥ 20)."); return; }
    setShowResume(false);
    runPlan({ remaining_km: rk });
  };

  useEffect(() => { loadSavedTrips(); }, []); // eslint-disable-line

  useEffect(() => { if (params.get("from") && params.get("to")) runPlan(); }, []); // eslint-disable-line

  const totalStops = useMemo(() => (plan?.timeline?.length || 0), [plan]);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <PlatformHeader
        brand={{ name: t("rt.brand_name", { defaultValue: "Roadtrip Planner" }), to: "/roadtrip" }}
        nav={[]}
        showBookCta={false}
        showCart={false}
      />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Route className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
          <h1 className="font-serif-display text-3xl text-stone-900 dark:text-stone-100" data-testid="roadtrip-title">
            {t("rt.page_title", { defaultValue: "Aussie EV Road-trip Planner" })}
          </h1>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <label className="text-[12px] font-medium block">
              {t("rt.from", { defaultValue: "From" })}
              <PlaceAutocomplete
                testid="roadtrip-from"
                value={_looksLikeLatLng(fromLabel) ? "" : fromLabel}
                onChange={(raw, label) => {
                  if (label) {
                    // User picked a suggestion — raw is "lat,lng".
                    setFromLabel(label);
                    setForm((f) => ({ ...f, from: raw }));
                  } else {
                    // Free-text edit — mirror to both so the backend
                    // fuzzy-resolver still gets a shot on submit.
                    setFromLabel(raw);
                    setForm((f) => ({ ...f, from: raw }));
                  }
                }}
                placeholder={t("rt.from_placeholder", { defaultValue: "Suburb, town or city (e.g. Newcastle NSW)" })}
              />
            </label>
            <label className="text-[12px] font-medium block">
              {t("rt.to", { defaultValue: "To" })}
              <PlaceAutocomplete
                testid="roadtrip-to"
                value={_looksLikeLatLng(toLabel) ? "" : toLabel}
                onChange={(raw, label) => {
                  if (label) {
                    setToLabel(label);
                    setForm((f) => ({ ...f, to: raw }));
                  } else {
                    setToLabel(raw);
                    setForm((f) => ({ ...f, to: raw }));
                  }
                }}
                placeholder={t("rt.to_placeholder", { defaultValue: "Suburb, town or city (e.g. Hobart TAS)" })}
              />
            </label>
          </div>

          {/* Waypoints — up to wpLimit intermediate stops. Free tier
              caps at 5; Pro at 20 (see hp_roadtrip._TIER_LIMITS). */}
          <div className="mb-3" data-testid="roadtrip-waypoints">
            {waypoints.map((wp, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <div className="text-[10px] font-bold text-stone-500 dark:text-stone-400 pt-3 w-10 shrink-0">
                  {t("rt.stop", { defaultValue: "STOP" })} {i + 1}
                </div>
                <div className="flex-1">
                  <PlaceAutocomplete
                    testid={`roadtrip-wp-${i}`}
                    value={_looksLikeLatLng(wp.label) ? "" : wp.label}
                    onChange={(raw, label) => setWaypointAt(i, raw, label)}
                    placeholder={t("rt.stop_placeholder", { defaultValue: "Intermediate stop" })}
                  />
                </div>
                <button
                  type="button"
                  data-testid={`roadtrip-wp-remove-${i}`}
                  onClick={() => removeWaypoint(i)}
                  className="h-9 w-9 rounded-lg border border-stone-300 dark:border-stone-700 text-stone-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600 mt-4 shrink-0"
                  aria-label="Remove stop"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="roadtrip-add-waypoint"
                onClick={addWaypoint}
                disabled={waypoints.length >= wpLimit}
                className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
              >
                + {t("rt.add_stop", { defaultValue: "Add stop" })} ({waypoints.length}/{wpLimit})
              </button>
              {waypoints.length >= wpLimit && form.tier === "free" && (
                <span className="text-[11px] text-amber-700 dark:text-amber-400">
                  {t("rt.upgrade_pro", { defaultValue: "Upgrade to Pro for up to 20 stops" })}
                </span>
              )}
              <button
                type="button"
                data-testid="roadtrip-reverse"
                onClick={reverseTrip}
                className="ml-auto text-[12px] font-semibold text-stone-700 dark:text-stone-300 hover:underline"
              >
                ↔ {t("rt.reverse", { defaultValue: "Reverse (calculate way home)" })}
              </button>
              <button
                type="button"
                data-testid="roadtrip-resume-here"
                onClick={resumeFromHere}
                className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                📍 {t("rt.from_current", { defaultValue: "From current location" })}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <label className="text-[12px] font-medium">
              {t("rt.range_per_charge", { defaultValue: "Range per charge (km)" })}
              <input
                data-testid="roadtrip-range"
                type="number" min={50} max={1200}
                value={form.range_km}
                onChange={(e) => setForm({ ...form, range_km: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
              />
            </label>
            <label className="text-[12px] font-medium">
              {t("rt.connector", { defaultValue: "Connector" })}
              <select
                data-testid="roadtrip-connector"
                value={form.connector}
                onChange={(e) => setForm({ ...form, connector: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
              >
                <option value="">{t("rt.connector_any", { defaultValue: "Any" })}</option>
                <option>CCS</option><option>Type2</option>
                <option>CHAdeMO</option><option>Tesla</option>
              </select>
            </label>
            <label className="text-[12px] font-medium">
              {t("rt.drive_hours_day", { defaultValue: "Drive hours/day" })}
              <input
                data-testid="roadtrip-daily-hours"
                type="number" min={2} max={14}
                value={form.daily_hours}
                onChange={(e) => setForm({ ...form, daily_hours: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
              />
            </label>
            <label className="text-[12px] font-medium">
              {t("rt.accommodation", { defaultValue: "Accommodation" })}
              <select
                data-testid="roadtrip-accom"
                value={form.accom_type}
                onChange={(e) => setForm({ ...form, accom_type: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
              >
                <option value="any">{t("rt.accom_any", { defaultValue: "Any (BnB · Hotel · Caravan)" })}</option>
                <option value="bnb">{t("rt.accom_bnb", { defaultValue: "BnB" })}</option>
                <option value="hotel">{t("rt.accom_hotel", { defaultValue: "Hotel" })}</option>
                <option value="caravan_park">{t("rt.accom_caravan", { defaultValue: "Caravan Park" })}</option>
              </select>
            </label>
            {/* Rating filter — only relevant for BnB/Hotel picks. Rated
                properties get filtered; unrated ones always pass so the
                corridor doesn't go empty in rural areas where OSM has
                no star tags. */}
            {(form.accom_type === "any"
              || form.accom_type === "bnb"
              || form.accom_type === "hotel") && (
              <label className="text-[12px] font-medium"
                      data-testid="roadtrip-min-stars-label">
                {t("rt.min_rating", { defaultValue: "Minimum rating" })}
                <select
                  data-testid="roadtrip-min-stars"
                  value={form.min_stars ?? 0}
                  onChange={(e) => setForm({
                    ...form, min_stars: Number(e.target.value),
                  })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                >
                  <option value={0}>{t("rt.any_rating", { defaultValue: "Any rating" })}</option>
                  <option value={3}>3★+</option>
                  <option value={3.5}>3.5★+</option>
                  <option value={4}>4★+</option>
                  <option value={4.5}>4.5★+</option>
                  <option value={5}>{t("rt.only_5star", { defaultValue: "5★ only" })}</option>
                </select>
              </label>
            )}
          </div>

          {/* Schedule — depart-at OR arrive-by (mutually exclusive). */}
          <div className="rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/60 p-3 mb-3">
            <div className="flex flex-wrap items-center gap-4 mb-2 text-[12px]">
              <span className="font-semibold text-stone-700 dark:text-stone-300">
                {t("rt.schedule", { defaultValue: "Schedule:" })}
              </span>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="sched-mode"
                  data-testid="roadtrip-sched-depart"
                  checked={form.schedule_mode === "depart"}
                  onChange={() => setForm({ ...form, schedule_mode: "depart" })}
                />
                {t("rt.depart_at", { defaultValue: "Depart at" })}
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="sched-mode"
                  data-testid="roadtrip-sched-arrive"
                  checked={form.schedule_mode === "arrive"}
                  onChange={() => setForm({ ...form, schedule_mode: "arrive" })}
                />
                {t("rt.arrive_by", { defaultValue: "Arrive by" })}
              </label>
              {form.schedule_mode === "depart" ? (
                <input
                  data-testid="roadtrip-depart-at"
                  type="datetime-local"
                  value={form.depart_at}
                  onChange={(e) => setForm({ ...form, depart_at: e.target.value })}
                  className="px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                />
              ) : (
                <input
                  data-testid="roadtrip-arrive-by"
                  type="datetime-local"
                  value={form.arrive_by}
                  onChange={(e) => setForm({ ...form, arrive_by: e.target.value })}
                  className="px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                />
              )}
            </div>
            {form.sleepover && (
              <div className="flex flex-wrap gap-4 text-[11px] text-stone-600 dark:text-stone-400">
                <label className="flex items-center gap-1.5">
                  {t("rt.hotel_checkin", { defaultValue: "Hotel check-in" })}
                  <input
                    data-testid="roadtrip-checkin-time"
                    type="time"
                    value={form.checkin_time}
                    onChange={(e) => setForm({ ...form, checkin_time: e.target.value })}
                    className="px-2 py-0.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                  />
                </label>
                <label className="flex items-center gap-1.5">
                  {t("rt.hotel_checkout", { defaultValue: "Hotel check-out" })}
                  <input
                    data-testid="roadtrip-checkout-time"
                    type="time"
                    value={form.checkout_time}
                    onChange={(e) => setForm({ ...form, checkout_time: e.target.value })}
                    className="px-2 py-0.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                  />
                </label>
                <span className="italic text-stone-500">
                  {t("rt.overnight_hint", { defaultValue: "Overnight legs won't schedule you outside these hours." })}
                </span>
              </div>
            )}
          </div>

          {/* Family + attractions (iter 264) */}
          <div className="rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/60 p-3 mb-3"
                data-testid="roadtrip-family-section">
            <p className="text-[11px] uppercase tracking-widest font-bold text-stone-500 dark:text-stone-400 mb-2">
              👨‍👩‍👧 {t("rt.whos_in_car", { defaultValue: "Who's in the car?" })}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-[12px]">
              <label className="flex items-center gap-1.5">
                {t("rt.adults", { defaultValue: "Adults" })}
                <input
                  data-testid="roadtrip-adults"
                  type="number" min={0} max={8}
                  value={form.adults}
                  onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })}
                  className="w-14 px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                />
              </label>
              <label className="flex items-center gap-1.5">
                {t("rt.kids", { defaultValue: "Kids" })}
                <input
                  data-testid="roadtrip-kids"
                  type="number" min={0} max={8}
                  value={form.kids}
                  onChange={(e) => setForm({ ...form, kids: Number(e.target.value) })}
                  className="w-14 px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                />
              </label>
              {form.kids > 0 && (
                <label className="flex items-center gap-1.5">
                  {t("rt.ages", { defaultValue: "Ages" })}
                  <input
                    data-testid="roadtrip-kid-ages"
                    type="text"
                    value={form.kid_ages}
                    onChange={(e) => setForm({ ...form, kid_ages: e.target.value })}
                    placeholder="4, 7, 11"
                    className="w-24 px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                  />
                </label>
              )}
              <label className="flex items-center gap-2 ml-2">
                <input
                  type="checkbox"
                  data-testid="roadtrip-attractions"
                  checked={form.include_attractions}
                  onChange={(e) => setForm({ ...form, include_attractions: e.target.checked })}
                />
                {t("rt.show_attractions", { defaultValue: "Show tourist attractions" })}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="roadtrip-kid-activities"
                  checked={form.include_kid_activities}
                  onChange={(e) => setForm({ ...form, include_kid_activities: e.target.checked })}
                  disabled={form.kids === 0}
                />
                {t("rt.show_kids", { defaultValue: "Show kid-friendly stops" })}
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center text-[12px]">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="roadtrip-sleepover"
                checked={form.sleepover}
                onChange={(e) => setForm({ ...form, sleepover: e.target.checked })}
              />
              {t("rt.overnight_stays", { defaultValue: "Overnight stays" })}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="roadtrip-fast-only"
                checked={form.fast_only}
                onChange={(e) => setForm({ ...form, fast_only: e.target.checked })}
              />
              {t("rt.fast_chargers_only", { defaultValue: "Fast chargers only" })}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="roadtrip-hotel-charger"
                checked={form.hotel_charger_only}
                onChange={(e) => setForm({ ...form, hotel_charger_only: e.target.checked })}
              />
              {t("rt.hotel_must_charger", { defaultValue: "Hotel must have charger" })}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="roadtrip-pet-friendly"
                checked={form.pet_friendly}
                onChange={(e) => setForm({ ...form, pet_friendly: e.target.checked })}
              />
              🐾 {t("rt.pet_only", { defaultValue: "Pet-friendly stays only" })}
            </label>
            <label className="flex items-center gap-2">
              {t("rt.safety", { defaultValue: "Safety" })}
              <select
                data-testid="roadtrip-safety"
                value={form.safety_pct}
                onChange={(e) => setForm({ ...form, safety_pct: Number(e.target.value) })}
                className="px-2 py-1 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                title="Buffer to allow for hills, weight and heat. 85% = plan on 340km from a 400km-nominal EV."
              >
                <option value={90}>{t("rt.safety_90", { defaultValue: "90% (flat)" })}</option>
                <option value={85}>{t("rt.safety_85", { defaultValue: "85% (default)" })}</option>
                <option value={80}>{t("rt.safety_80", { defaultValue: "80% (hills / towing)" })}</option>
                <option value={70}>{t("rt.safety_70", { defaultValue: "70% (heavy load)" })}</option>
              </select>
            </label>
            <button
              type="button"
              data-testid="roadtrip-save-open"
              onClick={() => setSaveOpen(true)}
              disabled={!plan}
              className="text-[12px] font-semibold text-stone-700 dark:text-stone-300 hover:underline disabled:opacity-40"
              title="Save this trip to your profile"
            >
              💾 {t("rt.save_trip", { defaultValue: "Save trip" })}
            </button>
            <button
              onClick={() => runPlan()}
              disabled={loading}
              data-testid="roadtrip-plan-btn"
              className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-[13px] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
              {loading
                ? t("rt.planning", { defaultValue: "Planning…" })
                : plan ? "↻ " + t("rt.refresh_trip", { defaultValue: "Refresh trip" })
                       : t("rt.plan_my_trip", { defaultValue: "Plan my trip" })}
            </button>
          </div>
        </div>

        {/* Resume mid-trip modal — asks for remaining km. */}
        {showResume && (
          <div
            data-testid="roadtrip-resume-modal"
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
            onClick={() => setShowResume(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-stone-900 rounded-2xl p-6 max-w-md w-full"
            >
              <h3 className="font-bold text-lg text-stone-900 dark:text-stone-100 mb-1">
                {t("rt.resume_title", { defaultValue: "Resume trip from here" })}
              </h3>
              <p className="text-[13px] text-stone-600 dark:text-stone-400 mb-4">
                {t("rt.resume_body", { defaultValue: "We captured your current location. Enter the remaining range shown on your dash so we plan the next leg against what you actually have — not a full charge." })}
              </p>
              <label className="text-[12px] font-medium block">
                {t("rt.remaining_range", { defaultValue: "Remaining range (km)" })}
                <input
                  data-testid="roadtrip-remaining-km"
                  type="number" min={20} max={1000}
                  value={remainingKm || ""}
                  onChange={(e) => setRemainingKm(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                  placeholder="e.g. 260"
                  autoFocus
                />
              </label>
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => setShowResume(false)}
                  className="px-4 py-2 text-[13px] rounded-lg border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800"
                >
                  {t("rt.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  data-testid="roadtrip-resume-apply"
                  onClick={applyResume}
                  className="px-4 py-2 text-[13px] rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
                >
                  {t("rt.replan_from_here", { defaultValue: "Replan from here" })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save-trip modal — name + save. */}
        {saveOpen && (
          <div
            data-testid="roadtrip-save-modal"
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
            onClick={() => setSaveOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-stone-900 rounded-2xl p-6 max-w-md w-full"
            >
              <h3 className="font-bold text-lg text-stone-900 dark:text-stone-100 mb-1">
                {t("rt.save_modal_title", { defaultValue: "Name this trip" })}
              </h3>
              <p className="text-[13px] text-stone-600 dark:text-stone-400 mb-4">
                {t("rt.save_modal_body", { defaultValue: "Saved trips live under your profile — reopen any time and hit Replan." })}
              </p>
              <input
                data-testid="roadtrip-save-name"
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                autoFocus
                className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800"
                placeholder={t("rt.save_name_placeholder", { defaultValue: "e.g. Brisbane → Sydney via Byron" })}
              />
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => setSaveOpen(false)}
                  className="px-4 py-2 text-[13px] rounded-lg border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800"
                >
                  {t("rt.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  data-testid="roadtrip-save-submit"
                  onClick={saveTrip}
                  disabled={!saveName.trim()}
                  className="px-4 py-2 text-[13px] rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold disabled:opacity-50"
                >
                  {t("rt.save_trip", { defaultValue: "Save trip" })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Saved trips strip — one row of chips, only when logged in. */}
        {savedTrips.length > 0 && (
          <div
            data-testid="roadtrip-saved-trips"
            className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/60 p-4 mb-6"
          >
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-stone-500 dark:text-stone-400 mb-2">
              {t("rt.your_saved_trips", { defaultValue: "Your saved trips" })}
            </p>
            <div className="flex flex-wrap gap-2">
              {savedTrips.map((tr) => (
                <div
                  key={tr.id}
                  className="flex items-center gap-1 border border-stone-200 dark:border-stone-700 rounded-full pl-3 pr-1 py-1 text-[12px]"
                >
                  <button
                    onClick={() => loadTrip(tr)}
                    className="text-stone-800 dark:text-stone-200 font-semibold"
                    data-testid={`roadtrip-saved-load-${tr.id}`}
                  >
                    {tr.name}
                  </button>
                  <span className="text-stone-400 text-[11px]">
                    {_looksLikeLatLng(tr.from) ? t("rt.custom_location", { defaultValue: "Custom location" }) : tr.from} → {_looksLikeLatLng(tr.to) ? t("rt.custom_location", { defaultValue: "Custom location" }) : tr.to}
                  </span>
                  <button
                    onClick={() => deleteTrip(tr.id)}
                    className="w-6 h-6 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50"
                    aria-label="Delete"
                    data-testid={`roadtrip-saved-delete-${tr.id}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {err && (
          <p data-testid="roadtrip-error" className="text-red-700 dark:text-red-400 text-[13px] mb-4">
            {err}
          </p>
        )}

        {plan && (
          <>
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-5 mb-6">
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-emerald-800 dark:text-emerald-300 mb-2">
                {t("rt.trip_summary", { defaultValue: "Trip summary" })}
              </p>
              <p
                data-testid="roadtrip-summary-route"
                className="text-stone-900 dark:text-stone-100 font-semibold text-lg"
              >
                {(_looksLikeLatLng(plan.from.label) ? (fromLabel && !_looksLikeLatLng(fromLabel) ? fromLabel : "Selected origin") : plan.from.label)}
                {" "}→{" "}
                {(_looksLikeLatLng(plan.to.label)   ? (toLabel   && !_looksLikeLatLng(toLabel)   ? toLabel   : "Selected destination") : plan.to.label)}
              </p>
              <p className="text-[13px] text-stone-700 dark:text-stone-300 mt-1">
                {plan.total_km} km · {formatHours(plan.est_drive_hours)} driving
                {" "}· {plan.chargers_in_corridor} {t("rt.chargers_in_corridor", { defaultValue: "chargers in corridor" })}
                {" "}· {totalStops} {t("rt.planned_stops", { defaultValue: "planned stops" })}
                {plan.sleepover ? ` · ${plan.sleepover_stops.length} ${plan.sleepover_stops.length === 1 ? t("rt.overnight_stop", { defaultValue: "overnight stop" }) : t("rt.overnight_stops", { defaultValue: "overnight stops" })}` : ""}
              </p>

              {plan.schedule && plan.schedule.mode !== "none" && (
                <p
                  data-testid="roadtrip-summary-schedule"
                  className="text-[13px] font-semibold text-emerald-900 dark:text-emerald-200 mt-2"
                >
                  {plan.schedule.mode === "arrive"
                    ? <>🕒 Leave <b>{formatDT(plan.schedule.depart_at)}</b> · arrive <b>{formatDT(plan.schedule.arrive_at)}</b></>
                    : <>🕒 Depart <b>{formatDT(plan.schedule.depart_at)}</b> · arrive at destination <b>{formatDT(plan.schedule.arrive_at)}</b></>
                  }
                </p>
              )}

              {/* Backend-emitted warnings (range violations, no chargers, etc.). */}
              {(plan.warnings || []).length > 0 && (
                <div
                  data-testid="roadtrip-warnings"
                  className="mt-3 space-y-2"
                >
                  {plan.warnings.map((w, i) => {
                    // Split on **bold** markers so we can render <b> inline.
                    const parts = (w.message || "").split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <div
                        key={i}
                        className={`text-[12px] flex items-start gap-1.5 leading-snug rounded-lg px-3 py-2 ${
                          w.level === "critical"
                            ? "bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-800"
                            : "bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-800"
                        }`}
                      >
                        <span aria-hidden="true" className="mt-[1px]">
                          {w.level === "critical" ? "⚠️" : "ℹ️"}
                        </span>
                        <div className="flex-1">
                          <span>
                            {parts.map((p, j) => (
                              p.startsWith("**") && p.endsWith("**")
                                ? <b key={j}>{p.slice(2, -2)}</b>
                                : <span key={j}>{p}</span>
                            ))}
                          </span>
                          {w.code === "leg_exceeds_range" && w.suggest_waypoint_name && (
                            <button
                              type="button"
                              data-testid={`roadtrip-add-suggested-waypoint-${i}`}
                              onClick={() => {
                                // Append the suggested suburb as a new waypoint
                                // and immediately re-plan.
                                const suburb = w.suggest_waypoint_name;
                                setWaypoints((wps) => {
                                  if (wps.length >= wpLimit) return wps;
                                  return [...wps, { raw: suburb, label: suburb }];
                                });
                                setTimeout(runPlan, 50);
                              }}
                              className="mt-2 inline-flex items-center gap-1 rounded-md bg-white dark:bg-stone-800 border border-red-300 dark:border-red-700 px-2 py-1 text-[11px] font-semibold text-red-800 dark:text-red-200 hover:bg-red-50 dark:hover:bg-red-950/40"
                            >
                              ➕ Add {w.suggest_waypoint_name} as waypoint & replan
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {plan.affiliate && !plan.affiliate.enabled && (
                <p className="text-[11px] text-stone-500 dark:text-stone-400 mt-2 italic">
                  {plan.affiliate.note}
                </p>
              )}
            </div>

            <div className="space-y-3" data-testid="roadtrip-timeline">
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-stone-500 dark:text-stone-400 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> {t("rt.itinerary", { defaultValue: "Itinerary" })}
              </p>
              {plan.timeline.length === 0 && (
                <p className="text-[13px] text-stone-500 dark:text-stone-400 italic">
                  {t("rt.no_stops_needed", { defaultValue: "No stops needed — the whole trip fits inside your range." })}
                </p>
              )}
              {plan.timeline.map((s) => <StopCard key={`${s.kind}-${s.slug || s.source_id || s.km_from_origin}`} stop={s} />)}
            </div>

            {/* Suggested detours — user-toggleable attractions + kid venues */}
            {plan.suggested_stops?.length > 0 && (
              <div className="mt-8" data-testid="roadtrip-suggested">
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-stone-500 dark:text-stone-400 flex items-center gap-2 mb-3">
                  ✨ {t("rt.suggested_detours", { defaultValue: "Suggested detours — tick to add" })}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {plan.suggested_stops.map((sp) => {
                    const slug = sp.slug || sp.source_id;
                    const on = selectedDetours.includes(slug);
                    return (
                      <label
                        key={slug}
                        data-testid={`roadtrip-suggest-${slug}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                          on
                            ? "border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/40"
                            : "border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 hover:border-emerald-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            const next = on
                              ? selectedDetours.filter((x) => x !== slug)
                              : [...selectedDetours, slug];
                            setSelectedDetours(next);
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-stone-900 dark:text-stone-100 truncate">
                            {sp.name}
                          </p>
                          <p className="text-[11px] text-stone-500 dark:text-stone-400 truncate">
                            {sp.friendly_location}
                          </p>
                          <p className="text-[10px] text-stone-500 mt-1 uppercase tracking-wider">
                            {sp.kind === "kid_venue" ? "👶 Kids" : "🎡 Attraction"}
                            {" · "}
                            {sp.km_from_origin} km from start
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {selectedDetours.length > 0 && (
                  <button
                    onClick={() => runPlan()}
                    data-testid="roadtrip-replan-with-detours"
                    className="mt-4 px-5 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-[13px]"
                  >
                    {t("rt.replan_with_detours", { defaultValue: "Replan with detours" })} ({selectedDetours.length})
                  </button>
                )}
              </div>
            )}

            {/* Launch driving mode — huge-font phone-in-cradle view */}
            <div className="mt-8 pt-6 border-t border-stone-200 dark:border-stone-800 flex flex-wrap gap-3">
              <Link
                to={`/roadtrip/drive?${params.toString()}`}
                data-testid="roadtrip-open-drive-mode"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 dark:bg-white text-white dark:text-stone-900 font-semibold text-sm hover:opacity-90"
              >
                🚗 {t("rt.open_driving", { defaultValue: "Open driving mode" })}
              </Link>
              <p className="text-[11px] text-stone-500 dark:text-stone-400 self-center italic">
                {t("rt.big_font_hint", { defaultValue: "Big-font, cradle-friendly. Works with CarPlay/Android Auto browser mirror." })}
              </p>
            </div>
          </>
        )}
      </main>

      {/* Global 4-column Suite footer with Trivago partnership line
       *  (page-context-aware, A/B tested — see components/SuiteFooter). */}
      <SuiteFooter trivagoContext="travel" />
    </div>
  );
}
